import {
  CosmosChain,
  Environment,
  EvmChain,
  GasToken,
} from "@axelar-network/axelarjs-sdk";
import { Network } from "alchemy-sdk";

import {
  ArbitrumLogoIMG,
  AvalancheLogoIMG,
  BaseLogoIMG,
  BNBChainLogoIMG,
  EthereumLogoIMG,
  FantomLogoIMG,
  ImmutablezkEVMLogoIMG,
  JunoLogoIMG,
  LineaLogoIMG,
  MantleLogoIMG,
  MoonbeamLogoIMG,
  OptimismLogoIMG,
  OsmosisLogoIMG,
  PolygonLogoIMG,
  PolygonzkEVMLogoIMG,
  RoninLogoIMG,
  ScrollLogoIMG,
  SkaleLogoIMG,
  SolanaLogoIMG,
  UnknownLogoIMG,
  ZKSyncLogoIMG,
} from "../assets";
import {
  BlockchainEcosystem,
  ChainData,
  ChainType,
  CosmosChainData,
  EvmChainData,
  SupportedChains,
} from "../interfaces";

export const getChainDataById = (
  chainId: string,
  network: string = BlockchainEcosystem.Evm,
): ChainData => {
  return (
    supportedChainsMap[network][chainId] || {
      chainId: "0",
      axelarName: EvmChain.ETHEREUM,
      axelarBridgeKey: "Unknown",
      displayName: "Unknown",
      fullName: "Unknown",
      image: UnknownLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#ffffff",
      rpcUrl: "",
      getTransactionExplorerUrl: () => "",
      tokenSymbol: GasToken.USDC,
      layerZeroChainId: "0",
    }
  );
};

export const getChainsDataByType = (
  chainType: ChainType,
  network: string = BlockchainEcosystem.Evm,
): ChainData[] => {
  return Object.values(supportedChainsMap[network] as ChainData[]).filter(
    (chain) => chain.chainType === chainType,
  );
};

// @TODO add `const allChains = getAllSupportedChains();` here.
// It is commonly used everywhere in the project and it should not be duplicated.
// It is quite a big object.
export const getAllSupportedChains = (): Record<
  string,
  EvmChainData | CosmosChainData
> => {
  return Object.values(supportedChainsMap).reduce(
    (acc, val) => ({ ...acc, ...val }),
    {},
  );
};

export const getChainIdEvmFilterOptions = (env: Environment) =>
  Object.values(supportedChainsMap.evm)
    .filter((chainData) => chainData.chainType === env)
    .map((chainData) => chainData.chainId);

export const getChainIdCosmosFilterOptions = (env: Environment) =>
  Object.values(supportedChainsMap.cosmos)
    .filter((chainData) => chainData.chainType === env)
    .map((chainData) => chainData.chainId);

export const getChainIdSolanaFilterOptions = (env: Environment) =>
  Object.values(supportedChainsMap.solana)
    .filter((chainData) => chainData.chainType === env)
    .map((chainData) => chainData.chainId);

export const getChainIdFilterOptions = (env: Environment) => [
  ...getChainIdEvmFilterOptions(env),
  ...getChainIdCosmosFilterOptions(env),
  ...getChainIdSolanaFilterOptions(env),
];

// @TODO it should be fetched from the API
export const supportedChainsMap: SupportedChains = {
  evm: {
    "1": {
      axelarName: EvmChain.ETHEREUM,
      axelarBridgeKey: "Ethereum",
      marketplaceGatewayKey: "Ethereum",
      displayName: "Ethereum",
      fullName: "Ethereum",
      chainId: "1",
      image: EthereumLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#6e6f76",
      rpcUrl: "https://rpc.ankr.com/eth",
      getTransactionExplorerUrl: (tx) => `https://etherscan.io/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      alchemyNetwork: Network.ETH_MAINNET,
      layerZeroChainId: "101",
    },
    "56": {
      axelarName: EvmChain.BINANCE,
      axelarBridgeKey: "binance",
      marketplaceGatewayKey: "Binance",
      displayName: "BNB Chain",
      fullName: "BNB Chain",
      chainId: "56",
      image: BNBChainLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#f0ba03",
      rpcUrl: "https://1rpc.io/bnb",
      getTransactionExplorerUrl: (tx) => `https://bscscan.com/tx/${tx}`,
      tokenSymbol: GasToken.BINANCE,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "102",
    },
    "97": {
      axelarName: EvmChain.BINANCE,
      axelarBridgeKey: "binance",
      marketplaceGatewayKey: "Binance",
      displayName: "BNB Chain (Testnet)",
      fullName: "BNB Chain (Testnet)",
      chainId: "97",
      image: BNBChainLogoIMG,
      chainType: Environment.TESTNET,
      chainColor: "#f0ba03",
      rpcUrl: "https://data-seed-prebsc-1-s1.binance.org:8545/",
      getTransactionExplorerUrl: (tx) => `https://testnet.bscscan.com/tx/${tx}`,
      tokenSymbol: GasToken.BINANCE,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "10102",
    },
    "137": {
      axelarName: EvmChain.POLYGON,
      axelarBridgeKey: "Polygon",
      marketplaceGatewayKey: "Polygon",
      displayName: "Polygon",
      fullName: "Polygon",
      chainId: "137",
      image: PolygonLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#8247e5",
      rpcUrl: "https://polygon-rpc.com/",
      getTransactionExplorerUrl: (tx) => `https://polygonscan.com/tx/${tx}`,
      tokenSymbol: GasToken.MATIC,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "109",
    },
    "324": {
      axelarName: "zksync" as any, // TODO fix when supported EvmChain.ZKSYNC
      axelarBridgeKey: "zkSync",
      marketplaceGatewayKey: "zkSync",
      displayName: "zkSync Era",
      fullName: "zkSync Era",
      image: ZKSyncLogoIMG,
      chainId: "324",
      chainType: Environment.MAINNET,
      chainColor: "#ffffff",
      rpcUrl: "https://mainnet.era.zksync.io",
      getTransactionExplorerUrl: (tx) => `https://explorer.zksync.io/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "165",
    },
    "280": {
      axelarName: "zksync" as any, // TODO fix when supported EvmChain.ZKSYNC
      axelarBridgeKey: "zkSync",
      marketplaceGatewayKey: "zkSync",
      displayName: "zkSync Era Testnet",
      fullName: "zkSync Era Testnet",
      image: ZKSyncLogoIMG,
      chainId: "280",
      chainType: Environment.TESTNET,
      chainColor: "#ffffff",
      rpcUrl: "https://testnet.era.zksync.dev",
      getTransactionExplorerUrl: (tx) =>
        `https://goerli.explorer.zksync.io/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "10165",
    },
    "80001": {
      axelarName: EvmChain.POLYGON,
      axelarBridgeKey: "Polygon",
      marketplaceGatewayKey: "Polygon",
      displayName: "Mumbai",
      fullName: "Mumbai",
      chainId: "80001",
      image: PolygonLogoIMG,
      chainType: Environment.TESTNET,
      chainColor: "#8247e5",
      rpcUrl: "https://polygon-mumbai.gateway.tenderly.co",
      getTransactionExplorerUrl: (tx) =>
        `https://mumbai.polygonscan.com/tx/${tx}`,
      tokenSymbol: GasToken.MATIC,
      ecosystem: BlockchainEcosystem.Evm,
      alchemyNetwork: Network.MATIC_MUMBAI,
      layerZeroChainId: "10109",
    },
    "43114": {
      axelarName: EvmChain.AVALANCHE,
      axelarBridgeKey: "Avalanche",
      marketplaceGatewayKey: "Avalanche",
      displayName: "Avalanche",
      fullName: "Avalanche",
      chainId: "43114",
      image: AvalancheLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#e84142",
      rpcUrl: "https://api.avax.network/ext/bc/C/rpc",
      getTransactionExplorerUrl: (tx) => `https://snowtrace.io/tx/${tx}`,
      tokenSymbol: GasToken.AVAX,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "106",
    },
    "43113": {
      axelarName: EvmChain.AVALANCHE,
      axelarBridgeKey: "Avalanche",
      marketplaceGatewayKey: "Avalanche",
      displayName: "Fuji",
      fullName: "Fuji",
      chainId: "43113",
      image: AvalancheLogoIMG,
      chainType: Environment.TESTNET,
      chainColor: "#e84142",
      rpcUrl: "https://api.avax-test.network/ext/bc/C/rpc",
      getTransactionExplorerUrl: (tx) =>
        `https://testnet.avascan.info/blockchain/c/tx/${tx}`,
      tokenSymbol: GasToken.AVAX,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "10106",
    },
    "1284": {
      axelarName: EvmChain.MOONBEAM,
      axelarBridgeKey: "Moonbeam",
      marketplaceGatewayKey: "Moonbeam",
      displayName: "Moonbeam",
      fullName: "Moonbeam",
      chainId: "1284",
      image: MoonbeamLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#52cac7",
      rpcUrl: "https://rpc.api.moonbeam.network",
      getTransactionExplorerUrl: (tx) => `https://moonscan.io/tx/${tx}`,
      tokenSymbol: GasToken.GLMR,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "126",
    },
    "1287": {
      axelarName: EvmChain.MOONBEAM,
      axelarBridgeKey: "Moonbeam",
      marketplaceGatewayKey: "Moonbeam",
      displayName: "Moonbase",
      fullName: "Moonbase",
      chainId: "1287",
      image: MoonbeamLogoIMG,
      chainType: Environment.TESTNET,
      chainColor: "#52cac7",
      rpcUrl: "https://moonbeam-alpha.api.onfinality.io/public",
      getTransactionExplorerUrl: (tx) =>
        `https://moonbase-blockscout.testnet.moonbeam.network/tx/${tx}`,
      tokenSymbol: GasToken.GLMR,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "10126",
    },
    "250": {
      axelarName: EvmChain.FANTOM,
      axelarBridgeKey: "Fantom",
      marketplaceGatewayKey: "Fantom",
      displayName: "Fantom",
      fullName: "Fantom",
      chainId: "250",
      image: FantomLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#13b5ec",
      rpcUrl: "https://rpc.ftm.tools/",
      getTransactionExplorerUrl: (tx) => `https://ftmscan.com/tx/${tx}`,
      tokenSymbol: GasToken.FTM,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "112",
    },
    "4002": {
      axelarName: EvmChain.FANTOM,
      axelarBridgeKey: "Fantom",
      marketplaceGatewayKey: "Fantom",
      displayName: "Fantom (Testnet)",
      fullName: "Fantom (Testnet)",
      chainId: "4002",
      image: FantomLogoIMG,
      chainType: Environment.TESTNET,
      chainColor: "#13b5ec",
      rpcUrl: "https://rpc.testnet.fantom.network",
      getTransactionExplorerUrl: (tx) => `https://testnet.ftmscan.com/tx/${tx}`,
      tokenSymbol: GasToken.FTM,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "10112",
    },
    "42161": {
      axelarName: "Arbitrum" as any, // EvmChain.Arbitrum, // TODO fix when supported
      axelarBridgeKey: "Arbitrum",
      marketplaceGatewayKey: "Arbitrum",
      displayName: "Arbitrum",
      fullName: "Arbitrum",
      chainId: "42161",
      image: ArbitrumLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#28a0f0",
      rpcUrl: "https://arb1.arbitrum.io/rpc",
      getTransactionExplorerUrl: (tx) => `https://arbiscan.io/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "110",
    },
    "10": {
      axelarName: "Optimism" as any, // EvmChain.Optimism, // TODO fix when supported
      axelarBridgeKey: "optimism",
      marketplaceGatewayKey: "Optimism",
      displayName: "Optimism",
      fullName: "Optimism",
      chainId: "10",
      image: OptimismLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#ff0420",
      rpcUrl: "https://mainnet.optimism.io",
      getTransactionExplorerUrl: (tx) =>
        `https://optimistic.etherscan.io/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "111",
    },
    "8453": {
      axelarName: "Base" as any, // EvmChain.Base, // TODO fix when supported
      axelarBridgeKey: "base",
      marketplaceGatewayKey: "NONE",
      displayName: "Base",
      fullName: "Base",
      chainId: "8453",
      image: BaseLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#779eff",
      rpcUrl: "https://base.meowrpc.com",
      getTransactionExplorerUrl: (tx) => `https://basescan.org/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "184",
    },
    "59144": {
      axelarName: "Linea" as any, // EvmChain.Linea, // TODO fix when supported
      axelarBridgeKey: "linea",
      marketplaceGatewayKey: "NONE",
      displayName: "Linea",
      fullName: "Linea",
      chainId: "59144",
      image: LineaLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#000000",
      rpcUrl: "https://linea.drpc.org",
      getTransactionExplorerUrl: (tx) => `https://lineascan.build/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "183",
    },
    "534352": {
      axelarName: "Scroll" as any, // EvmChain.Scroll, // TODO fix when supported
      axelarBridgeKey: "scroll",
      marketplaceGatewayKey: "NONE",
      displayName: "Scroll",
      fullName: "Scroll",
      chainId: "534352",
      image: ScrollLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#ffeeda",
      rpcUrl: "https://scroll.blockpi.network/v1/rpc/public",
      getTransactionExplorerUrl: (tx) =>
        `https://blockscout.scroll.io/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "214",
    },
    "1101": {
      axelarName: "polygonzkEVM" as any, // EvmChain.polygonzkEVM, // TODO fix when supported
      axelarBridgeKey: "polygonzkEVM",
      marketplaceGatewayKey: "NONE",
      displayName: "Polygon zkEVM",
      fullName: "Polygon zkEVM",
      chainId: "1101",
      image: PolygonzkEVMLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#803ce0",
      rpcUrl: "https://polygon-zkevm.drpc.org",
      getTransactionExplorerUrl: (tx) =>
        `https://zkevm.polygonscan.com/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "158",
    },
    "5000": {
      axelarName: "mantle" as any, // EvmChain.Mantle, // TODO fix when supported
      axelarBridgeKey: "mantle",
      marketplaceGatewayKey: "NONE",
      displayName: "Mantle",
      fullName: "Mantle",
      chainId: "5000",
      image: MantleLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#ffffff",
      rpcUrl: "https://rpc.mantle.xyz",
      getTransactionExplorerUrl: (tx) => `https://mantlescan.info/tx/${tx}`,
      tokenSymbol: "MNT",
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "181",
    },
    "1482601649": {
      axelarName: "skale" as any, // EvmChain.Arbitrum, // TODO fix when supported
      axelarBridgeKey: "skale",
      marketplaceGatewayKey: "NONE",
      displayName: "Skale",
      fullName: "Skale Nebula Hub",
      chainId: "1482601649",
      image: SkaleLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#000000",
      rpcUrl: "https://mainnet.skalenodes.com/v1/green-giddy-denebola",
      getTransactionExplorerUrl: (tx) =>
        `https://green-giddy-denebola.explorer.mainnet.skalenodes.com/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "273",
    },
    "2020": {
      axelarName: "ronin" as any, // EvmChain.Arbitrum, // TODO fix when supported
      axelarBridgeKey: "ronin",
      marketplaceGatewayKey: "NONE",
      displayName: "Ronin",
      fullName: "Ronin",
      chainId: "2020",
      image: RoninLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#000000",
      rpcUrl: "https://ronin.drpc.org",
      getTransactionExplorerUrl: (tx) => `https://app.roninchain.com/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "273",
    },
    "13371": {
      axelarName: "immutable zkEVM" as any, // EvmChain.Arbitrum, // TODO fix when supported
      axelarBridgeKey: "immutable zkEVM",
      marketplaceGatewayKey: "NONE",
      displayName: "Immutable zkEVM",
      fullName: "Immutable zkEVM",
      chainId: "13371",
      image: ImmutablezkEVMLogoIMG,
      chainType: Environment.MAINNET,
      chainColor: "#000000",
      rpcUrl: "https://rpc.immutable.com",
      getTransactionExplorerUrl: (tx) => `https://immutascan.io/tx/${tx}`,
      tokenSymbol: GasToken.ETH,
      ecosystem: BlockchainEcosystem.Evm,
      layerZeroChainId: "273",
    },
  },
  cosmos: {
    "juno-1": {
      addressPrefix: "juno",
      axelarName: CosmosChain.JUNO,
      axelarBridgeKey: "Juno", // TODO could be wrong
      marketplaceGatewayKey: "Juno",
      displayName: "Juno",
      fullName: "Juno",
      image: JunoLogoIMG,
      chainId: "juno-1",
      chainType: Environment.MAINNET,
      chainColor: "#000000",
      rpcUrl: "https://juno-rpc.publicnode.com:443",
      getTransactionExplorerUrl: (tx) =>
        `https://www.mintscan.io/osmosis/txs/${tx}`,
      tokenSymbol: GasToken.AVAX, // TODO could be wrong
      gasPrice: "0.025ujuno",
      ecosystem: BlockchainEcosystem.Cosmos,
    },
    "uni-5": {
      addressPrefix: "juno",
      axelarName: CosmosChain.JUNO,
      axelarBridgeKey: "JunoTestnet", // TODO could be wrong
      marketplaceGatewayKey: "JunoTestnet",
      displayName: "Juno Testnet",
      fullName: "Juno Testnet",
      image: JunoLogoIMG,
      chainId: "uni-5",
      chainType: Environment.TESTNET,
      chainColor: "#000000",
      rpcUrl: "https://rpc.uni.junonetwork.io/",
      getTransactionExplorerUrl: (tx) =>
        `https://testnet.mintscan.io/juno-testnet/txs/${tx}`,
      tokenSymbol: GasToken.AVAX, // TODO could be wrong
      gasPrice: "0.025ujunox",
      ecosystem: BlockchainEcosystem.Cosmos,
    },
    "osmosis-1": {
      addressPrefix: "osmo",
      axelarName: CosmosChain.OSMOSIS,
      axelarBridgeKey: "Osmosis",
      marketplaceGatewayKey: "Osmosis",
      displayName: "Osmosis",
      fullName: "Osmosis",
      image: OsmosisLogoIMG,
      chainId: "osmosis-1",
      chainType: Environment.MAINNET,
      chainColor: "#461798",
      rpcUrl: "https://osmosis-rpc.publicnode.com:443",
      getTransactionExplorerUrl: (tx) =>
        `https://celatone.osmosis.zone/osmosis-1/txs/${tx}`,
      tokenSymbol: GasToken.AVAX, // TODO could be wrong
      gasPrice: "0.025uosmo",
      ecosystem: BlockchainEcosystem.Cosmos,
    },
    "harpoon-4": {
      addressPrefix: "kuji",
      axelarName: CosmosChain.JUNO,
      axelarBridgeKey: "KujiraTestnet", // TODO could be wrong
      marketplaceGatewayKey: "KujiraTestnet",
      displayName: "Kujira Testnet",
      fullName: "Kujira Testnet",
      image: JunoLogoIMG,
      chainId: "harpoon-4",
      chainType: Environment.TESTNET,
      chainColor: "#000000",
      rpcUrl: "https://rpc.kujira.pupmos.network",
      getTransactionExplorerUrl: (tx) =>
        `https://finder.kujira.app/harpoon-4/txs/${tx}`,
      tokenSymbol: GasToken.AVAX, // TODO could be wrong
      gasPrice: "0.025ukuji",
      ecosystem: BlockchainEcosystem.Cosmos,
    },
  },
  solana: {
    devnet: {
      displayName: "Devnet",
      fullName: "Solana Devnet",
      image: SolanaLogoIMG,
      chainId: "devnet",
      chainType: Environment.TESTNET,
      chainColor: "#9945FF",
      rpcUrl: "https://api.devnet.solana.com",
      rpcWsUrl: "wss://api.devnet.solana.com",
      getTransactionExplorerUrl: (tx) =>
        `https://solscan.io/tx/${tx}?cluster=devnet`,
      tokenSymbol: "SOL",
      ecosystem: BlockchainEcosystem.Solana,
    },
    testnet: {
      displayName: "Testnet",
      fullName: "Solana Testnet",
      image: SolanaLogoIMG,
      chainId: "testnet",
      chainType: Environment.TESTNET,
      chainColor: "#9945FF",
      rpcUrl: "https://api.testnet.solana.com",
      getTransactionExplorerUrl: (tx) =>
        `https://solscan.io/tx/${tx}?cluster=testnet`,
      tokenSymbol: "SOL",
      ecosystem: BlockchainEcosystem.Solana,
    },
    "mainnet-beta": {
      displayName: "Solana",
      fullName: "Solana",
      image: SolanaLogoIMG,
      chainId: "mainnet-beta",
      chainType: Environment.MAINNET,
      chainColor: "#9945FF",
      rpcUrl:
        "https://orbital-thrilling-water.solana-mainnet.quiknode.pro/6b4a6d73d2cbe0590ce45bad3b375b183acef83f/",
      rpcWsUrl:
        "wss://orbital-thrilling-water.solana-mainnet.quiknode.pro/6b4a6d73d2cbe0590ce45bad3b375b183acef83f/",
      getTransactionExplorerUrl: (tx) => `https://solscan.io/tx/${tx}`,
      tokenSymbol: "SOL",
      ecosystem: BlockchainEcosystem.Solana,
    },
  },
};
